import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import track from 'utils/track';
import { getAnnouncementHeadingId } from 'utils/announcement/getHeadingId';
import Box from 'components/Box';
import Flex from 'components/Flex';
import Card from 'components/Card';
import Text from 'components/Text';
import TextLink from 'components/TextLink';
import TooltipButton from 'components/TooltipButton';
import Icon from 'components/Icon';
import { SpinnerDot } from 'components/icons';
import Flag from 'components/Flag';
import VisuallyHidden from 'components/VisuallyHidden';
import useSearchParameter from 'hooks/useSearchParameter';
import { announcementQueryKeys, useUpdateUserAnnouncementState, useFetchAnnouncement } from 'actions/announcements';
import PlatformAnnouncementModal from 'screens/PlatformAnnouncementModal';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function AnnouncementBanner(_ref) {
  var announcement = _ref.announcement,
    isDismissable = _ref.isDismissable;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var queryClient = useQueryClient();
  var headingId = getAnnouncementHeadingId(announcement.id);
  var readLabelId = "".concat(headingId, "-read-label");
  var _useSearchParameter = useSearchParameter('announcement-modal'),
    _useSearchParameter2 = _slicedToArray(_useSearchParameter, 2),
    selectedAnnouncementId = _useSearchParameter2[0],
    setSelectedAnnouncementId = _useSearchParameter2[1];
  var hasAnnouncementModal = selectedAnnouncementId === announcement.id;
  var _useFetchAnnouncement = useFetchAnnouncement({
      announcementId: announcement.id,
      enabled: hasAnnouncementModal
    }),
    selectedAnnouncement = _useFetchAnnouncement.data,
    isLoadingSelectedAnnouncement = _useFetchAnnouncement.isLoading;
  var _useUpdateUserAnnounc = useUpdateUserAnnouncementState({
      announcementId: announcement.id
    }),
    updateUserAnnouncementState = _useUpdateUserAnnounc.updateUserAnnouncementState;
  function handleLinkClick() {
    // Mark as read only first time user clicks on the announcement
    if (!announcement.isRead) {
      updateUserAnnouncementState({
        is_read: true
      });
      track('announcement', 'read', announcement.id);
    }
    setSelectedAnnouncementId(announcement.id);
  }
  function handleDismiss() {
    updateUserAnnouncementState({
      is_dismissed: true
    });
    track('announcement', 'dismissed', announcement.id);
  }
  function closeModal() {
    queryClient.invalidateQueries({
      queryKey: announcementQueryKeys.announcements
    });
    setSelectedAnnouncementId(undefined);
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Card, {
      variant: "clickable",
      px: "m",
      py: 12,
      position: "relative",
      children: [!announcement.isRead && /*#__PURE__*/_jsx(Box, {
        backgroundColor: "links",
        position: "absolute",
        left: true,
        top: true,
        bottom: true,
        width: 4
      }), /*#__PURE__*/_jsx(Flag, {
        align: "top",
        token: isLoadingSelectedAnnouncement ? /*#__PURE__*/_jsx(SpinnerDot, {
          vAlign: true,
          color: "links"
        }) : /*#__PURE__*/_jsx(Icon, {
          vAlign: true,
          name: "megaphone",
          color: "links"
        }),
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "row",
          wrap: true,
          gap: "xs",
          mr: "s",
          children: [/*#__PURE__*/_jsxs(TextLink, {
            id: headingId,
            bold: true,
            as: "button",
            onClick: handleLinkClick,
            "aria-describedby": readLabelId,
            children: [t(announcement.title), /*#__PURE__*/_jsx(Box
            // Extend clickable area across whole banner
            , {
              fillParent: true
            })]
          }), /*#__PURE__*/_jsx(Text, {
            children: t(announcement.callOut)
          }), !!announcement.isRead && /*#__PURE__*/_jsxs(VisuallyHidden, {
            id: readLabelId,
            children: [' ', t('state_read_brackets')]
          })]
        })
      }), isDismissable && /*#__PURE__*/_jsx(TooltipButton, {
        iconOnly: true,
        icon: "x",
        size: "medium",
        variant: "transparent",
        onClick: handleDismiss,
        position: "absolute",
        right: "xxs",
        top: "xs",
        children: t('actions_dismiss')
      })]
    }), hasAnnouncementModal && selectedAnnouncement && /*#__PURE__*/_jsx(PlatformAnnouncementModal, {
      announcement: selectedAnnouncement,
      onClose: closeModal
    })]
  });
}
export default AnnouncementBanner;